import React from 'react';
import { FONT_MONO } from '../../views/landing/landingData';
import { Eyebrow, Icons, Spinner } from './studioUi';

interface FileSearchProps {
    /** Flat relative paths, same list the tree renders. */
    files: string[];
    /** Read a file's current text (WebContainer VFS). */
    readFile: (path: string) => Promise<string>;
    /** Content search needs the VFS to be mounted; false => filenames only. */
    canSearchContent: boolean;
    /** Open a file, optionally scrolled to a 1-based line. */
    onOpen: (path: string, line?: number) => void;
    /** Rendered when the box is empty — i.e. the normal file tree. */
    children: React.ReactNode;
}

interface ContentHit {
    path: string;
    line: number;
    text: string;
}

/** Extensions worth grepping. Anything else is binary or generated noise. */
const TEXT_EXT = new Set([
    '.astro', '.tsx', '.ts', '.jsx', '.js', '.mjs', '.cjs',
    '.css', '.scss', '.json', '.md', '.mdx', '.html', '.txt', '.svg', '.yml', '.yaml', '.env',
]);
const MAX_FILE_BYTES = 200_000;
const MAX_HITS_PER_FILE = 5;
const MAX_TOTAL_HITS = 100;
const MAX_NAME_HITS = 40;
const READ_CONCURRENCY = 8;

const ext = (p: string) => {
    const i = p.lastIndexOf('.');
    return i === -1 ? '' : p.slice(i).toLowerCase();
};

/**
 * Filename + file-content search for the code panel.
 *
 * Content search runs entirely in the browser against the WebContainer VFS —
 * the same source `selectFile` already reads from — so it needs no API and sees
 * edits as soon as they are flushed (300ms after typing). It is additive: with an
 * empty query this renders `children` untouched, so the tree behaves exactly as
 * before.
 */
export const FileSearch: React.FC<FileSearchProps> = ({ files, readFile, canSearchContent, onOpen, children }) => {
    const [query, setQuery] = React.useState('');
    const [scanning, setScanning] = React.useState(false);
    const [hits, setHits] = React.useState<ContentHit[]>([]);
    const [truncated, setTruncated] = React.useState(false);
    // Bumped on every new search so a superseded scan discards its results.
    const runId = React.useRef(0);

    const q = query.trim();
    const nameHits = React.useMemo(() => {
        if (!q) return [];
        const needle = q.toLowerCase();
        return files.filter((f) => f.toLowerCase().includes(needle)).slice(0, MAX_NAME_HITS);
    }, [files, q]);

    React.useEffect(() => {
        // One character matches almost everything — not worth reading every file.
        if (q.length < 2 || !canSearchContent) {
            setHits([]);
            setScanning(false);
            setTruncated(false);
            return;
        }
        const run = ++runId.current;
        const timer = setTimeout(async () => {
            setScanning(true);
            const needle = q.toLowerCase();
            const targets = files.filter((f) => TEXT_EXT.has(ext(f)));
            const found: ContentHit[] = [];
            let cut = false;

            for (let i = 0; i < targets.length; i += READ_CONCURRENCY) {
                if (runId.current !== run) return; // superseded
                const batch = targets.slice(i, i + READ_CONCURRENCY);
                const texts = await Promise.all(batch.map((p) => readFile(p).catch(() => '')));
                batch.forEach((path, k) => {
                    const text = texts[k];
                    if (!text || text.length > MAX_FILE_BYTES) return;
                    if (!text.toLowerCase().includes(needle)) return; // cheap reject
                    let perFile = 0;
                    const lines = text.split('\n');
                    for (let n = 0; n < lines.length; n++) {
                        if (!lines[n].toLowerCase().includes(needle)) continue;
                        if (perFile >= MAX_HITS_PER_FILE || found.length >= MAX_TOTAL_HITS) {
                            cut = true;
                            break;
                        }
                        perFile++;
                        found.push({ path, line: n + 1, text: lines[n].trim().slice(0, 200) });
                    }
                });
                if (found.length >= MAX_TOTAL_HITS) {
                    cut = true;
                    break;
                }
            }
            if (runId.current !== run) return;
            setHits(found);
            setTruncated(cut);
            setScanning(false);
        }, 250);

        return () => clearTimeout(timer);
    }, [q, files, readFile, canSearchContent]);

    // Group content hits by file so the list reads like a normal search panel.
    const grouped = React.useMemo(() => {
        const map = new Map<string, ContentHit[]>();
        for (const h of hits) {
            const list = map.get(h.path);
            if (list) list.push(h);
            else map.set(h.path, [h]);
        }
        return [...map.entries()];
    }, [hits]);

    const hintStyle: React.CSSProperties = { margin: 0, padding: '0 12px 8px', fontSize: 11.5, color: 'var(--hv-muted)' };
    const hitRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, minHeight: 28, padding: '0 12px', fontSize: 12.5, color: 'var(--hv-body)', cursor: 'pointer', borderRadius: 8 };

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 'none', padding: '8px 8px 4px' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: 32, padding: '0 10px', gap: 7, borderRadius: 999, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                    <span style={{ flex: 'none', display: 'flex', color: 'var(--hv-muted)' }}>{Icons.search({})}</span>
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
                        spellCheck={false}
                        placeholder="Search files or text…"
                        aria-label="Search files or text"
                        style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontSize: 12.5, color: 'var(--hv-ink)' }}
                    />
                    {query && (
                        <button type="button" onClick={() => setQuery('')} title="Clear search" aria-label="Clear search" className="hv-h-brand-ink" style={{ flex: 'none', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, padding: 0, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer' }}>
                            {Icons.x({ size: 13, sw: 2.2 })}
                        </button>
                    )}
                </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {!q ? (
                    children
                ) : (
                    <div style={{ padding: '6px 6px 10px' }}>
                        {/* Filename matches */}
                        <p style={{ margin: 0, padding: '6px 12px 4px' }}><Eyebrow>FILE NAMES ({nameHits.length})</Eyebrow></p>
                        {nameHits.length === 0 && <p style={hintStyle}>No matching file names.</p>}
                        {nameHits.map((p) => (
                            <div key={p} role="button" tabIndex={0} onClick={() => onOpen(p)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(p)} className="hv-h-page2" style={hitRow}>
                                <span style={{ flex: 'none', display: 'flex', color: 'var(--hv-muted)' }}>{Icons.file({})}</span>
                                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p}>{p}</span>
                            </div>
                        ))}

                        {/* Content matches */}
                        <p style={{ margin: 0, padding: '14px 12px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Eyebrow>IN FILES ({hits.length}{truncated ? '+' : ''})</Eyebrow>
                            {scanning && <Spinner size={11} />}
                        </p>
                        {!canSearchContent && <p style={hintStyle}>Waiting for the preview to be ready before searching file contents.</p>}
                        {canSearchContent && q.length < 2 && <p style={hintStyle}>Type at least 2 characters.</p>}
                        {canSearchContent && q.length >= 2 && !scanning && hits.length === 0 && <p style={hintStyle}>No matches.</p>}
                        {grouped.map(([path, list]) => (
                            <div key={path} style={{ paddingBottom: 4 }}>
                                <p style={{ margin: 0, padding: '4px 12px', fontSize: 11.5, color: 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={path}>{path}</p>
                                {list.map((h) => (
                                    <div key={`${h.path}:${h.line}`} role="button" tabIndex={0} onClick={() => onOpen(h.path, h.line)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(h.path, h.line)} className="hv-h-page2" style={{ ...hitRow, minHeight: 24, paddingLeft: 24, gap: 8, alignItems: 'flex-start' }}>
                                        <span style={{ flex: 'none', fontFamily: FONT_MONO, fontSize: 10, color: 'var(--hv-muted)', paddingTop: 5 }}>{h.line}</span>
                                        <span style={{ flex: 1, minWidth: 0, fontFamily: FONT_MONO, fontSize: 11, lineHeight: 1.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingTop: 3 }}>{h.text}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
